<script lang="ts">
	import type { RouteSelectorType } from '$lib/components/route/routeSelector/types';
	import type { ClassValue } from 'svelte/elements';
	import Badge from '$lib/components/atoms/badge/Badge.svelte';
	import SkeletonBlock from '$lib/components/atoms/skeleton/SkeletonBlock.svelte';
	import Icon from '$lib/components/icons/Icon.svelte';
	import Card from '$lib/components/route/routeSelector/components/CardTemplate.svelte';
	import ListGroup from '$lib/components/route/routeSelector/components/ListGroup.svelte';
	import RouteGroup from '$lib/components/route/routeSelector/components/RouteGroup.svelte';

	interface Props {
		departure: string;
		arrival: string;
		numberOfWaypoints?: number;
		class?: ClassValue;
		badgeLabels?: string[];
		routes?: RouteSelectorType[];
		selectedRouteIndex?: number;
		isLoading?: boolean;
		collapsed?: boolean;
		errorType?: 'noRoad' | 'noAddress';
		onClickOnSummary?: () => void;
		onIndexChange?: (index: number) => void;
		onToggleRouteSelector?: (isCollapsed: boolean) => void;
	}

	const {
		departure,
		arrival,
		numberOfWaypoints,
		routes,
		selectedRouteIndex,
		isLoading = false,
		collapsed = false,
		badgeLabels,
		errorType,
		onClickOnSummary,
		onIndexChange,
		class: classes,
		onToggleRouteSelector,
	}: Props = $props();
</script>

<Card dataTestId="route-selector" class={['transition-max-height delay-20 duration-250 ease-out max-h-254px', collapsed && 'max-h-180px transition-max-height duration-250 delay-20 ease-in', classes]}>
	{#if isLoading}
		{@render routeSkeleton()}
	{:else}
		<RouteGroup
			{routes}
			{selectedRouteIndex}
			{collapsed}
			{errorType}
			{onClickOnSummary}
			{onIndexChange}
			{onToggleRouteSelector}
		>
			{#snippet summary()}
				<span class="grid grid-area-[header] grid-cols-[auto_auto_auto] justify-start font-size-3.5 text-coloredneutral font-400">
					{@render waypointItem(departure, undefined, 'departure')}
					<span class="inline-flex">
						{#if numberOfWaypoints && numberOfWaypoints > 0}
							{@render waypointSeparator()}
							{@render waypointItem(`+${numberOfWaypoints}`, 'flex-none', 'waypoints')}
						{/if}
						{@render waypointSeparator()}
					</span>
					{@render waypointItem(arrival, undefined, 'arrival')}
				</span>
				{#if badgeLabels}
					<span class="grid-area-[badge]">
						{#each badgeLabels as label (label)}
							<span class="badge text-coloredneutral font-size-3.5 font-400 p-0.5 rounded-sm bg-neutral-weak inline-block mr-0.5">{label}</span>
						{/each}
					</span>
				{/if}
			{/snippet}
			{#snippet durationColumn({ hours, minutes, datetime }, isDiscovery, isSelected)}
				<div class="flex items-center gap-1">
					<time class="font-size-5 font-600" {datetime}>
						{hours} <span class="font-size-4">h</span>
						{#if minutes && minutes > 0}
							{minutes < 10 ? `0${minutes}` : minutes} <span class="font-size-4">min</span>
						{/if}
					</time>

					{#if isDiscovery}
						<Badge
							icon="scenic-road"
							size="sm"
							class={['font-size-3.5 font-400 p-0  w-5 h-5', isSelected ? 'bg-coloredneutral-softest text-neutral-weakest' : 'bg-discovery-weak text-discovery']}
						/>
					{/if}
				</div>
			{/snippet}
			{#snippet costColumn(cost)}
				{#each cost as part, index (JSON.stringify(part) + index)}
					{@const isCurrency = part.type === 'currency'}
					<span class={isCurrency ? 'font-size-4' : 'font-size-5'}>{index !== 0 && isCurrency ? ' ' : ''}{part.value}</span>
				{/each}
			{/snippet}
			{#snippet distanceColumn({ value, unit })}
				<span class="font-size-5">{value} <span class="font-size-4">{unit}</span></span>
			{/snippet}
			{#snippet emissionsColumn(value)}
				<span class="font-size-5">{value} <span class="font-size-4">Kg <span class="font-size-3">CO<sub class="font-size-2.5 align-baseline">2</sub></span></span></span>
			{/snippet}
			{#snippet consumptionColumn(value)}
				<span class="font-size-5">{value} <span class="font-size-4">KWh</span></span>
			{/snippet}
		</RouteGroup>
	{/if}
</Card>

{#snippet waypointSeparator()}
	<Icon icon="chevron-right" tag="span" class="text-5 text-neutral min-w-5" />
{/snippet}
{#snippet waypointItem(text: string, classes?: string, dataTestId?: string)}
	<span class={[classes, 'truncate']} data-testid={dataTestId}>
		{text}
	</span>
{/snippet}

{#snippet routeSkeleton()}
	<ListGroup isLoading dataTestId="route-selector-skeleton">
		{#snippet header()}
			<SkeletonBlock height={'36px'} width={'36px'} class={'grid-area-[button] right-2 top-1'} />
			<span class="grid-area-[header] flex gap-x-1 pb-1.25">
				{#each [{ h: 16, w: 72 }, { h: 16, w: 11 }, { h: 16, w: 48 }] as departureAndArrivalProps, index (index)}
					<SkeletonBlock
						height={`${departureAndArrivalProps.h}px`}
						width={`${departureAndArrivalProps.w}px`}
						class={'inline-block'}
					/>
				{/each}
			</span>
			<span class="grid-area-[badge] flex gap-x-1 pb-3">
				{#each [{ h: 24, w: 60 }, { h: 24, w: 80 }, { h: 24, w: 60 }] as badgeProps, index (index)}
					<SkeletonBlock
						height={`${badgeProps.h}px`}
						width={`${badgeProps.w}px`}
						class={'inline-block'}
					/>
				{/each}
			</span>
		{/snippet}
		{#snippet listItem(classes: string, index: number)}
			<div class={['route-group-row grid grid-cols-4', classes, index !== 3 && 'h-12']}>
				{#each [{ h: 20, w: 80 }, { h: 20, w: 40, classes: 'mr-16.75' }, { h: 20, w: 60, classes: 'mr-11.75' }, { h: 20, w: 80 }] as lineProps, index (index)}
					<div class={['align-top', index === 0 && 'min-w-32 pl-2']}>
						<SkeletonBlock
							height={`${lineProps.h}px`}
							width={`${lineProps.w}px`}
							class={[lineProps.classes, 'inline-block']}
						/>
					</div>
				{/each}
			</div>
		{/snippet}
	</ListGroup>
{/snippet}
